import React, { useState, useEffect, useCallback } from "react";
import classnames from "classnames";
import { PulseLoader } from "react-spinners";
import { useToasts } from "react-toast-notifications";
import api from "../../api";
import User from "../../types/User";
import useDebounce from "../../helpers/useDebounce";
import endpoints, { axios } from "../../config/api";
import ProtectedComponent from "../../components/ProtectedComponent";
import AddTeamMemberModal from "../../components/Modal/AddTeamMemberModal";
import ConfirmDeleteModal from "../../components/Modal/ConfirmDeleteModal";
import ProjectTeamTabItem from "./ProjectTeamTabItem";
import { useOnlineProvider } from "../../state/OnlineProvider";

interface Props {
  projectId: number;
}

const ProjectTeamTab = ({ projectId }: Props) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState("");
  const { addToast } = useToasts();
  const { isOnline } = useOnlineProvider();

  useEffect(() => {
    setLoading(true);
    setUsers([]);
    api
      .getProjectUsers(projectId, debouncedSearchTerm)
      .then(({ payload }) => {
        setUsers([...payload]);
        setLoading(false);
      })
      .catch(_ => {
        addToast("Failed to get Project Users. Try again.", {
          appearance: "error",
          autoDismiss: true
        });
      });
  }, [addToast, debouncedSearchTerm, projectId]);

  const addUserToProject = useCallback(
    userId => {
      api
        .addUserToProject(projectId, userId)
        .then(() => {
          addToast("User Added To Project", {
            appearance: "success",
            autoDismiss: true
          });
          setSearchTerm("");
        })
        .catch(() => {
          addToast("An error occured when adding user to project.", {
            appearance: "error",
            autoDismiss: true
          });
        });
    },
    [addToast, projectId]
  );

  const removeUserFromProject = useCallback(() => {
    if (!userToRemove || userToRemove.length === 0) {
      addToast("Failed to remove user from project", {
        appearance: "error",
        autoDismiss: true
      });
      setIsDeleteModalOpen(false);
      return;
    }
    axios
      .delete(endpoints.project.removeUser(projectId), {
        data: [userToRemove]
      })
      .then(() => {
        setIsDeleteModalOpen(false);
        setUsers(users.filter(u => u.id !== userToRemove));
        addToast("User removed from project", {
          appearance: "success",
          autoDismiss: true
        });
      })
      .catch(() => {
        setIsDeleteModalOpen(false);
        addToast("Failed to remove user from project", {
          appearance: "error",
          autoDismiss: true
        });
      });
  }, [addToast, projectId, userToRemove, users]);

  return (
    <div>
      <div className="lg:flex lg:flex-row lg:justify-between mt-12">
        <ProtectedComponent requiredRole="PROJECTMANAGER">
          <ConfirmDeleteModal
            isModalOpen={isDeleteModalOpen}
            setIsModalOpen={setIsDeleteModalOpen}
            onConfirm={removeUserFromProject}
            entityLabel="User from the Project"
          />
          <AddTeamMemberModal
            closeModal={() => setIsAddMemberModalOpen(false)}
            isModalOpen={isAddMemberModalOpen}
            onSubmit={(selectedUser: User) => {
              setIsAddMemberModalOpen(false);
              setUsers([...users, selectedUser]);
              addUserToProject(selectedUser.id);
            }}
            projectId={projectId}
          />
          <button
            className={classnames("w-full lg:w-56", {
              "btn-secondary": isOnline,
              "btn-disabled": !isOnline
            })}
            disabled={!isOnline}
            onClick={() => setIsAddMemberModalOpen(true)}
          >
            + Add a team member
          </button>
        </ProtectedComponent>
        <div className="flex flex-col lg:flex-row lg:justify-end lg:items-center">
          <p className="mt-4 lg:my-0 lg:mr-4">Look up a user...</p>
          <input
            className="w-full lg:w-56"
            value={searchTerm}
            placeholder="Enter user's name"
            onChange={e => setSearchTerm(e.target.value)}
            onKeyPress={e => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
              }
            }}
          />
        </div>
      </div>
      <div className="mt-8 block">
        <table className="border-collapse space-x-1 mb-8">
          <thead>
            <tr className="bg-white">
              <th className="pb-3">Name</th>
              <th className="w-auto md:w-64 pb-3"></th>
              <th className="invisible sm:visible pb-3">Role</th>
              <th className="pb-3">Commercial notifications</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={3}>
                  <div className="w-full flex justify-center items-center">
                    <PulseLoader color="#00B2A9" />
                  </div>
                </td>
              </tr>
            )}
            {users && !loading && users.length === 0 && (
              <tr className="text-center ">
                <td colSpan={4}>No Users Found</td>
              </tr>
            )}
            {users &&
              users
                .sort((a: any, b: any) => {
                  if (a.firstName < b.firstName) {
                    return -1;
                  }
                  if (a.firstName > b.firstName) {
                    return 1;
                  }
                  return 0;
                })
                .map((item: any) => (
                  <ProjectTeamTabItem
                    setIsDeleteModalOpen={setIsDeleteModalOpen}
                    setUserToRemove={setUserToRemove}
                    item={item}
                    projectId={projectId}
                  />
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProjectTeamTab;
